import { apolloClient } from '@/vue-apollo';
import eventsTypeByIMEI from '@/graphql/eventsTypeByIMEI.gql';
import moment from '@/lib/time';
import Excel from 'exceljs';
import {
  fontNormal,
  sheetMargin,
  hightlightTableHead,
  imeiToPlateNumber,
  imeiToVehicleName,
  formatDate,
  formatDuration,
  hightlightRow,
  geofenceStartEndFromLocation,
  getMCRDriverName,
  blobToFile,
  generateLogoHeader,
  addLogoCompany,
} from './config';

function generateSummaryInfo({ resultsByImei, vehicles }) {
  /* summary by imei */
  const summaryByImei = {};
  Object.keys(resultsByImei).forEach((imei) => {
    const resultsByDate = {};

    resultsByImei[imei].forEach((row) => {
      const keyOfDate = moment.unix(row.startTime).startOf('day').unix();
      if (!(keyOfDate in resultsByDate)) {
        resultsByDate[keyOfDate] = {
          count: 0,
          totalTimeSec: 0,
        };
      }

      resultsByDate[keyOfDate].count++;
      resultsByDate[keyOfDate].totalTimeSec =
        resultsByDate[keyOfDate].totalTimeSec +
        parseInt(row.stopTime - row.startTime);
    });

    summaryByImei[imei] = Object.keys(resultsByDate).map((date) => {
      return {
        date: moment.unix(date).format('D MMM YYYY'),
        count: resultsByDate[date].count,
        totalTimeSec: resultsByDate[date].totalTimeSec,
      };
    });
  });

  /* summary all */
  const summaryAllTableData = [];
  let summaryCount = 0;
  let summaryTotalTimeSec = 0;

  const summaryInfoList = Object.keys(summaryByImei).map((imei) => {
    const plateNumber = imeiToPlateNumber(vehicles, parseInt(imei));
    const count = summaryByImei[imei].reduce(
      (acc, current) => acc + current.count,
      0
    );
    const totalTimeSec = summaryByImei[imei].reduce(
      (acc, current) => acc + current.totalTimeSec,
      0
    );
    return {
      plateNumber,
      count,
      totalTimeSec,
    };
  });
  summaryInfoList
    .sort((a, b) => b.totalTimeSec - a.totalTimeSec)
    .forEach((summaryObj, idx) => {
      summaryAllTableData.push([
        idx + 1,
        summaryObj.plateNumber,
        summaryObj.count,
        formatDuration(0, summaryObj.totalTimeSec),
      ]);

      summaryCount = summaryCount + summaryObj.count;
      summaryTotalTimeSec = summaryTotalTimeSec + summaryObj.totalTimeSec;
    });
  summaryAllTableData.push([
    null,
    'รวม',
    summaryCount,
    formatDuration(0, summaryTotalTimeSec),
  ]);

  return {
    columnSpec: [
      {
        key: 'no',
        width: 5,
        style: {
          numFmt: '0',
          font: fontNormal,
        },
      },
      {
        key: 'name',
        width: 20,
      },
      {
        key: 'count',
        width: 15,
      },
      {
        key: 'totalTimeSec',
        width: 15,
      },
    ],
    summaryByImei: Object.keys(summaryByImei).map((imei) => {
      return {
        title: `สรุปรายงานจอดรถติดเครื่อง ของ รถทะเบียน ${imeiToPlateNumber(
          vehicles,
          parseInt(imei)
        )}`,
        tableHead: ['ที่', 'วันที่', 'จำนวนที่เกิน', 'เวลารวม'],
        tableData: summaryByImei[imei].map((data, idx) => {
          return [
            idx + 1,
            data.date,
            data.count,
            formatDuration(0, data.totalTimeSec),
          ];
        }),
      };
    }),
    summaryAll: {
      title: 'สรุปรายงานจอดรถติดเครื่องของทะเบียนรถทั้งหมด',
      tableHead: ['ที่', 'ทะเบียน', 'จำนวนที่เกิน', 'เวลารวม'],
      tableData: summaryAllTableData,
    },
  };
}

function generateSheetInfo({
  resultsByImei,
  vehicles,
  geofences,
  dictOfDriver,
}) {
  const sheetInfoList = [];

  Object.keys(resultsByImei).forEach((imei) => {
    const results = resultsByImei[imei];

    const plateNumber = imeiToPlateNumber(vehicles, parseInt(imei));
    const vehicleName = imeiToVehicleName(vehicles, parseInt(imei));
    let totalTimeSec = 0;

    const tableRows = [];
    results.forEach((row) => {
      const resLoc = geofenceStartEndFromLocation(geofences, row);
      const mcr_license_no =
        typeof row.positionDataStart.mcr_license_no != 'undefined'
          ? row.positionDataStart.mcr_license_no
          : '';
      const mcr_driver_name = row.positionDataStart.mcr_driver_name || '';

      tableRows.push([
        plateNumber,
        vehicleName,
        formatDate(row.startTime),
        formatDate(row.stopTime),
        formatDuration(row.startTime, row.stopTime),
        resLoc.end,
        `${row.lat / 10000000}, ${row.lng / 10000000}`,
        typeof dictOfDriver != 'undefined' && mcr_license_no in dictOfDriver
          ? dictOfDriver[mcr_license_no].name
          : getMCRDriverName(mcr_license_no, mcr_driver_name),
        row.startTime,
        row.stopTime,
      ]);

      totalTimeSec = totalTimeSec + parseInt(row.stopTime - row.startTime);
    });

    sheetInfoList.push({
      sheetName: plateNumber,
      title: `รายงานจอดรถติดเครื่อง ของ รถทะเบียน ${plateNumber} เวลาเริ่ม ${formatDate(
        results[0].startTime
      )} ถึง ${formatDate(results[results.length - 1].stopTime)}`,
      meta: `รวมเวลา ${moment('1970-01-01')
        .startOf('day')
        .seconds(totalTimeSec)
        .format('HH:mm:ss')} ชม.:นาที:วินาที`,
      columnSpec: [
        {
          key: 'plate_number',
          width: 12,
          style: {
            font: fontNormal,
          },
        },
        {
          key: 'name',
          width: 22,
          style: {
            numFmt: '0',
          },
        },
        {
          key: 'start',
          width: 20,
        },
        {
          key: 'stop',
          width: 20,
        },
        {
          key: 'totalTime',
          width: 12,
        },
        {
          key: 'reversegeolocationend',
          width: 40,
        },
        {
          key: 'geolocation',
          width: 30,
        },
        {
          key: 'driver',
          width: 15,
        },
        {
          key: 'timeStampStart',
          width: 15,
        },
        {
          key: 'timeStampStop',
          width: 15,
        },
      ],
      tableHead: [
        'ทะเบียนรถ',
        'ชื่อ',
        'วัน เวลา เริ่มต้น',
        'วัน เวลา สิ้นสุด',
        'รวมเวลา',
        'ตำแหน่ง',
        'ละติจูด ลองจิจูด',
        'ผู้ใช้รถ',
        'timestamp เริ่มต้น',
        'timestamp สิ้นสุด',
      ],
      tableData: tableRows,
    });
  });

  return sheetInfoList;
}

export const newIdlingReport = async (
  eventTypes,
  vehicles,
  imeis,
  start,
  stop,
  duration,
  geofences,
  dictOfDriver,
  logo
) => {
  try {
    await apolloClient.resetStore();
    const rawData = await apolloClient
      .query({
        // Query
        query: eventsTypeByIMEI,
        variables: {
          eventTypes,
          imeis,
          start,
          stop,
        },
      })
      .then((eventData) =>
        eventData.data.eventsTypeByIMEI.filter(
          (row) => row.stopTime - row.startTime >= duration
        )
      )
      .catch((err) => {
        throw err;
      });

    const workbook = new Excel.Workbook();
    const pageProperties = {
      properties: {
        tabColor: {
          argb: 'FF41C173',
        },
      },
      pageSetup: {
        paperSize: 9,
        orientation: 'landscape',
      },
    };

    if (rawData.length > 0) {
      const resultsByImei = {};
      rawData.forEach((data) => {
        if (!(data.imei in resultsByImei)) {
          resultsByImei[data.imei] = [];
        }
        resultsByImei[data.imei].push(data);
      });

      const normalSheetInfoList = generateSheetInfo({
        resultsByImei,
        vehicles,
        geofences,
        dictOfDriver,
      });
      const summarySheetInfo = generateSummaryInfo({ resultsByImei, vehicles });

      /* summary sheet */
      const summarySheet = workbook.addWorksheet('สรุป', pageProperties);
      summarySheet.pageSetup.margins = sheetMargin;
      summarySheet.columns = summarySheetInfo.columnSpec;

      let summaryTitleRow = 1;

      summarySheetInfo.summaryByImei.forEach((data) => {
        summarySheet.addRow([data.title]);
        summarySheet.addRow(data.tableHead);
        summarySheet.addRows(data.tableData);
        summarySheet.addRows([[], []]);

        hightlightRow(summarySheet, summaryTitleRow);
        hightlightTableHead(summarySheet, summaryTitleRow + 1); // 1 row under title row

        /* next starting data table row is number of current header (title, table head, ...etc) rows + number of data rows + number of blank rows */
        summaryTitleRow = summaryTitleRow + 2 + data.tableData.length + 2;
      });

      summarySheet.addRow([summarySheetInfo.summaryAll.title]);
      summarySheet.addRow(summarySheetInfo.summaryAll.tableHead);
      summarySheet.addRows(summarySheetInfo.summaryAll.tableData);

      hightlightRow(summarySheet, summaryTitleRow);
      hightlightTableHead(summarySheet, summaryTitleRow + 1); // 1 row under title row

      /* normal sheet */
      let imageLogoId;

      if (logo && logo.url) {
        const ext = logo.ext.substring(1);
        const myBase64Image = await generateLogoHeader(logo);
        imageLogoId = workbook.addImage({
          base64: myBase64Image,
          extension: ext,
        });
      }

      normalSheetInfoList.forEach((sheetInfo) => {
        let startingRow = 1;

        const sheet = workbook.addWorksheet(
          sheetInfo.sheetName,
          pageProperties
        );
        sheet.pageSetup.margins = sheetMargin;
        sheet.columns = sheetInfo.columnSpec;

        if (logo && logo.url) {
          startingRow = 2;
          addLogoCompany(sheet, imageLogoId);
        }

        sheet.addRow([sheetInfo.title]);
        sheet.addRow([sheetInfo.meta]);
        sheet.addRow([]);
        sheet.addRow(sheetInfo.tableHead);
        sheet.addRows(sheetInfo.tableData);

        hightlightTableHead(sheet, startingRow + 3);
        hightlightRow(sheet, startingRow);
        hightlightRow(sheet, startingRow + 1);
      });
    } else {
      workbook.addWorksheet('ไม่พบข้อมูล', pageProperties);
    }

    const fileName = `Idling_Report_${
      moment.unix(start).format('D_MMM_YY') +
      '-ถึง-' +
      moment.unix(stop).format('D_MMM_YY')
    }`;
    return blobToFile(workbook, fileName);
  } catch (err) {
    console.error('newIdlingReport', err);
    return err;
  }
};

export default newIdlingReport;
